/**
 * In-memory stand-in for the 通知センター API.
 *
 * It mutates MOCK_NOTIFICATIONS so the screen behaves like the real thing: marking rows read drops the tab
 * badge, deleting removes them from every page, and 再送信 changes the 結果 of the rows it could actually
 * fix. Without that a reviewer cannot tell a working button from a decorative one.
 *
 * DELETE THIS FILE when the API lands. Nothing outside services/admin/notification-center imports it.
 */
import { MOCK_NOTIFICATIONS } from './mockData';
import {
  NOTIFICATION_TAB,
  type NotificationBulkResult,
  type NotificationItem,
  type NotificationListQuery,
  type NotificationListResult,
  type NotificationTab,
  type NotificationUnreadCounts,
} from '@/types/admin-notification-center';

/** Enough delay to see a spinner, not enough to be annoying while clicking around. */
const LATENCY_MS = 280;
const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/** Cloned, not aliased: the mutations below would otherwise edit the seed itself and mockReset would be a no-op. */
const clone = (): NotificationItem[] => MOCK_NOTIFICATIONS.map(item => ({ ...item }));

let store: NotificationItem[] = clone();

const byNewestFirst = (a: NotificationItem, b: NotificationItem) =>
  b.occurredAt.localeCompare(a.occurredAt);

/**
 * Inclusive on both ends, now to the MINUTE.
 *
 * The bounds arrive as 'YYYY-MM-DDTHH:mm' and the stored stamp is a full ISO string, so both are cut to the
 * same 16 characters before comparing — string order and chronological order agree in that shape. Cutting
 * the stamp rather than parsing it also makes 「to = 09:30」 include everything up to 09:30:59, which is what
 * someone typing a minute means.
 */
const withinRange = (iso: string, from: string, to: string): boolean => {
  const at = iso.slice(0, 16);
  if (from && at < from.slice(0, 16)) return false;
  if (to && at > to.slice(0, 16)) return false;
  return true;
};

/**
 * How a typed 受診者 term is compared with a stored one.
 *
 * NFKC does in one call what the real screen does with two mb_convert_kana passes
 * (ReturnRezeptRepository.php:57-58): half-width ﾀﾅｶ folds to タナカ, and full-width ４２３０ folds to 4230.
 * Someone typing a number off a printed 帳票 in whichever width their IME happened to be in still finds the
 * row.
 *
 * Spaces are then dropped, which the real screen does NOT do yet. A stored 氏名 is 「田中　太郎」 with an
 * ideographic space in the middle; nobody types that space, and today's LIKE '%田中太郎%' therefore returns
 * nothing. It is a small thing that reads as 「the search is broken」, so the mock shows the behaviour worth
 * having and this comment is the note for whoever writes the query.
 */
const fold = (value: string): string => value.normalize('NFKC').replace(/\s+/g, '').toLowerCase();

const matches = (item: NotificationItem, q: NotificationListQuery): boolean => {
  if (item.tab !== q.tab) return false;
  if (!withinRange(item.occurredAt, q.from, q.to)) return false;
  if (q.read === 'unread' && item.read) return false;
  if (q.read === 'read' && !item.read) return false;

  // コード / 名称 — partial match. On マスタ同期 the row's pair is targetCode/targetName (it may be a
  // 保険者, not a clinic); on the other two tabs it is always the clinic. Falling back rather than
  // branching on the tab keeps one predicate: whichever pair the row HAS is the pair that gets searched.
  //
  // A row carrying neither is filtered OUT once either box is used — asking for a code and being shown
  // rows that have none is the wrong answer. No kind produces such a row today (住所, the one that named
  // no record, left the tab on 2026-09-07); the predicate is kept because the columns are still nullable.
  const num = q.clinicNumber.trim().toLowerCase();
  if (num && !(item.clinicNumber ?? item.targetCode ?? '').toLowerCase().includes(num)) return false;

  const name = fold(q.clinicName.trim());
  if (name && !fold(item.clinicName ?? item.targetName ?? '').includes(name)) return false;

  if (q.serviceType && item.serviceType !== q.serviceType) return false;

  // ONE term, THREE targets — 受診者名, its カナ and 受診者番号 — the same three 返戻処理 searches from a
  // single box. カナ is not carried on the mock item, so only two are matched here; the backend has it.
  const patient = fold(q.patient.trim());
  if (patient) {
    const haystack = fold(`${item.patientName ?? ''} ${item.patientSerial ?? ''}`);
    if (!haystack.includes(patient)) return false;
  }

  return true;
};

export const mockFetchList = async (q: NotificationListQuery): Promise<NotificationListResult> => {
  await sleep(LATENCY_MS);

  // TWO SETS, and the difference is the whole of the summary rule.
  //
  // `scoped` is every filter EXCEPT 種別; `matched` adds it. The breakdown is counted over the first and the
  // list is drawn from the second, so pressing a chip narrows the rows without rewriting the other two
  // numbers to zero — see AdminNotificationCenterService::list, which the live tabs run and which this has
  // to keep agreeing with. Empty selection means ALL: nothing ticked cannot mean nothing matches, or the
  // list would go blank the moment someone clears the box.
  const scoped = store.filter(item => matches(item, q)).sort(byNewestFirst);
  const matched =
    q.levels.length === 0 ? scoped : scoped.filter(item => q.levels.includes(item.level));

  const start = (q.page - 1) * q.perPage;

  return {
    data: matched.slice(start, start + q.perPage),
    meta: {
      currentPage: q.page,
      perPage: q.perPage,
      total: matched.length,
      lastPage: Math.max(1, Math.ceil(matched.length / q.perPage)),
    },
    // Counted over EVERY row this search can reach, not the page and not the 種別 selection.
    summary: {
      info: scoped.filter(i => i.level === 'info').length,
      success: scoped.filter(i => i.level === 'success').length,
      error: scoped.filter(i => i.level === 'error').length,
    },
  };
};

export const mockFetchUnreadCounts = async (): Promise<NotificationUnreadCounts> => {
  await sleep(120);

  const counts = {
    [NOTIFICATION_TAB.MASTER]: 0,
    [NOTIFICATION_TAB.REZEPT]: 0,
    [NOTIFICATION_TAB.INBOUND]: 0,
  } as NotificationUnreadCounts;

  store.forEach(item => {
    if (!item.read) counts[item.tab as NotificationTab] += 1;
  });

  return counts;
};

export const mockMarkRead = async (ids: number[]): Promise<NotificationBulkResult> => {
  await sleep(LATENCY_MS);

  let affected = 0;
  store.forEach(item => {
    if (ids.includes(item.id) && !item.read) {
      item.read = true;
      affected += 1;
    }
  });

  return { requested: ids.length, affected, skipped: ids.length - affected };
};

/**
 * 再送信.
 *
 * Transient failures come good, validation failures do not — and the mock keeps that distinction on purpose.
 * A demo where every retry turns green teaches the operator the wrong lesson: the whole reason 再送信 sits
 * next to エラー詳細 is that half the failures on this screen need a data fix first, and pressing the button
 * on those just burns another attempt.
 */
const TRANSIENT_CODES = new Set(['CONNECTION_TIMEOUT', 'SERVER_ERROR']);

export const mockRetry = async (ids: number[]): Promise<NotificationBulkResult> => {
  await sleep(LATENCY_MS + 300);

  let affected = 0;
  let skipped = 0;

  store.forEach(item => {
    if (!ids.includes(item.id)) return;
    if (!item.retryable) {
      skipped += 1;
      return;
    }

    affected += 1;
    item.attempts += 1;
    item.read = true;

    if (TRANSIENT_CODES.has(item.errorDetail?.code ?? '')) {
      item.level = 'success';
      item.retryable = false;
      item.errorDetail = null;
      // The re-send closed the gap, so the run now reads as fully successful.
      item.successCount = item.totalCount;
      item.failureCount = item.totalCount === null ? null : 0;
      item.finishedAt = new Date().toISOString();
      item.summary = '';
    }
  });

  return { requested: ids.length, affected, skipped };
};

/** Test/demo helper — puts the dataset back the way it started. */
export const mockReset = (): void => {
  store = clone();
};
